import { useCallback, useEffect, useMemo, useState } from "react";
import { inTauri, isToolEnabled, loadAllTools, setToolEnabled, type Tool, type ToolSource } from "@/lib/tools";
import { apiFetch } from "@/lib/backend";

type ServerStatus = {
  name: string;
  config: { command: string; args?: string[]; env?: Record<string, string> };
  connected: boolean;
  toolCount: number;
};

const SOURCE_LABELS: Record<ToolSource, string> = {
  web: "Web",
  tauri: "Tauri",
  server: "Server",
  mcp: "MCP",
};

function estimateTokens(t: Tool): number {
  const schema = JSON.stringify({ name: t.name, description: t.description, parameters: t.schema });
  return Math.ceil(schema.length / 4);
}

export function ToolsPanelBody() {
  const [tools, setTools] = useState<Tool[]>([]);
  const [enabledSet, setEnabledSet] = useState<Set<string>>(new Set());
  const [servers, setServers] = useState<ServerStatus[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadingServer, setLoadingServer] = useState<Record<string, boolean>>({});
  const [editingConfig, setEditingConfig] = useState(false);
  const [configJson, setConfigJson] = useState("");
  const [configError, setConfigError] = useState<string | null>(null);
  const [expandedTool, setExpandedTool] = useState<string | null>(null);
  const [localConfigJson, setLocalConfigJson] = useState("");
  const [localConfigError, setLocalConfigError] = useState<string | null>(null);
  const [localSaved, setLocalSaved] = useState(false);

  const refreshTools = useCallback(async () => {
    const all = await loadAllTools();
    setTools(all);
    setEnabledSet(new Set(all.filter((t) => isToolEnabled(t.name)).map((t) => t.name)));
  }, []);

  const refreshServers = useCallback(async () => {
    const r = await apiFetch("/api/mcp/servers");
    if (r.ok) setServers(await r.json());
  }, []);

  const loadLocalConfig = useCallback(async () => {
    if (!inTauri) return;
    const { invoke } = await import("@tauri-apps/api/core");
    try { setLocalConfigJson(await invoke("local_mcp_get_config")); } catch {}
  }, []);

  useEffect(() => { refreshTools(); refreshServers(); loadLocalConfig(); }, [refreshTools, refreshServers, loadLocalConfig]);

  const saveLocalConfig = async () => {
    setLocalConfigError(null);
    const { invoke } = await import("@tauri-apps/api/core");
    try {
      await invoke("local_mcp_set_config", { content: localConfigJson });
      setLocalSaved(true);
      setTimeout(() => setLocalSaved(false), 1500);
      await refreshTools();
    } catch (e) {
      setLocalConfigError(e instanceof Error ? e.message : String(e));
    }
  };

  const toggle = (name: string) => {
    const next = !enabledSet.has(name);
    void setToolEnabled(name, next);
    setEnabledSet((prev) => {
      const s = new Set(prev);
      if (next) s.add(name); else s.delete(name);
      return s;
    });
  };

  const handleConnect = async (name: string) => {
    setLoadingServer((p) => ({ ...p, [name]: true }));
    setError(null);
    try {
      const res = await apiFetch("/api/mcp/connect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (data.error) setError(data.error);
      await Promise.all([refreshServers(), refreshTools()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Connection failed");
    }
    setLoadingServer((p) => ({ ...p, [name]: false }));
  };

  const handleDisconnect = async (name: string) => {
    await apiFetch("/api/mcp/disconnect", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
    await Promise.all([refreshServers(), refreshTools()]);
  };

  const startEditingConfig = () => {
    const config: Record<string, unknown> = {};
    for (const s of servers) config[s.name] = s.config;
    setConfigJson(JSON.stringify(config, null, 2));
    setConfigError(null);
    setEditingConfig(true);
  };

  const saveConfig = async () => {
    try {
      const parsed = JSON.parse(configJson);
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        setConfigError("Must be a JSON object");
        return;
      }
      await apiFetch("/api/mcp/servers", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(parsed),
      });
      setEditingConfig(false);
      await refreshServers();
    } catch {
      setConfigError("Invalid JSON");
    }
  };

  const grouped = useMemo(() => {
    const out: Record<ToolSource, Tool[]> = { web: [], tauri: [], server: [], mcp: [] };
    for (const t of tools) out[t.source].push(t);
    return out;
  }, [tools]);

  const enabledTotal = tools.filter((t) => enabledSet.has(t.name));
  const totalTokens = enabledTotal.reduce((s, t) => s + estimateTokens(t), 0);

  return (
    <div>
      <div className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>
          )}

          {(["web", "tauri", "server", "mcp"] as ToolSource[]).map((src) => {
            const list = grouped[src];
            if (list.length === 0) return null;
            return (
              <div key={src} className="space-y-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {SOURCE_LABELS[src]}
                </p>
                {list.map((t) => {
                  const isOn = enabledSet.has(t.name);
                  const isOpen = expandedTool === t.name;
                  return (
                    <div key={t.name} className="rounded-md border">
                      <div className="flex items-start gap-2 px-3 py-2">
                        <button
                          onClick={() => toggle(t.name)}
                          className={`mt-0.5 size-4 shrink-0 rounded border transition-colors ${
                            isOn ? "border-primary bg-primary" : "border-muted-foreground"
                          }`}
                        />

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => setExpandedTool(isOpen ? null : t.name)}
                              className="text-xs font-medium hover:text-primary text-left truncate"
                            >
                              {t.name}
                            </button>
                            <span className="text-[10px] text-muted-foreground/50">~{estimateTokens(t)} tok</span>
                          </div>
                          {t.description && (
                            <p className="text-[11px] text-muted-foreground leading-tight">{t.description}</p>
                          )}
                        </div>
                      </div>
                      {isOpen && (
                        <pre className="border-t bg-muted/40 px-3 py-2 text-[10px] text-muted-foreground overflow-x-auto max-h-48 overflow-y-auto font-mono">
{JSON.stringify(t.schema, null, 2)}
                        </pre>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {tools.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No tools available. Connect an MCP server below to discover tools.
            </p>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                MCP Servers
              </p>
              <button
                onClick={editingConfig ? () => setEditingConfig(false) : startEditingConfig}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                {editingConfig ? "Cancel" : "Edit JSON"}
              </button>
            </div>

            {editingConfig ? (
              <div className="space-y-2">
                <textarea
                  value={configJson}
                  onChange={(e) => { setConfigJson(e.target.value); setConfigError(null); }}
                  rows={10}
                  spellCheck={false}
                  className="w-full resize-y rounded-md border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-primary"
                />
                {configError && <p className="text-xs text-destructive">{configError}</p>}
                <div className="flex gap-2">
                  <button onClick={saveConfig} className="rounded-md bg-primary px-3 py-1 text-xs text-primary-foreground">
                    Save
                  </button>
                  <button onClick={() => setEditingConfig(false)} className="rounded-md px-3 py-1 text-xs text-muted-foreground">
                    Cancel
                  </button>
                </div>
              </div>
            ) : servers.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-2">
                No servers configured.{" "}
                <button onClick={startEditingConfig} className="text-primary hover:underline">Edit JSON</button>{" "}
                to add one.
              </p>
            ) : (
              servers.map((s) => (
                <div key={s.name} className="flex items-center gap-2 rounded-md border px-3 py-2">
                  <span className={`size-2 rounded-full ${s.connected ? "bg-green-500" : "bg-muted-foreground/30"}`} />
                  <span className="flex-1 text-sm font-medium truncate">{s.name}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {s.connected ? `${s.toolCount} tools` : "disconnected"}
                  </span>
                  {loadingServer[s.name] ? (
                    <i className="fa-solid fa-spinner-third fa-spin text-sm text-muted-foreground" />
                  ) : s.connected ? (
                    <button
                      onClick={() => handleDisconnect(s.name)}
                      className="rounded-md px-2 py-0.5 text-xs text-muted-foreground hover:text-destructive"
                    >
                      Disconnect
                    </button>
                  ) : (
                    <button
                      onClick={() => handleConnect(s.name)}
                      className="rounded-md px-2 py-0.5 text-xs text-primary hover:text-primary/80"
                    >
                      Connect
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {inTauri && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Local MCP (this device)
                </p>
                {localSaved && <span className="text-[11px] text-green-500">Saved</span>}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Runs on this Mac via the desktop app, not the server. Discovered tools appear under Tauri above.
              </p>
              <textarea
                value={localConfigJson}
                onChange={(e) => { setLocalConfigJson(e.target.value); setLocalConfigError(null); }}
                rows={8}
                spellCheck={false}
                className="w-full resize-y rounded-md border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-primary"
              />
              {localConfigError && <p className="text-xs text-destructive">{localConfigError}</p>}
              <button onClick={saveLocalConfig} className="rounded-md bg-primary px-3 py-1 text-xs text-primary-foreground">
                Save & reconnect
              </button>
            </div>
          )}
        </div>

      {enabledTotal.length > 0 && (
        <div className="mt-4 border-t pt-2 text-[11px] text-muted-foreground flex items-center justify-between">
          <span>{enabledTotal.length} tools enabled</span>
          <span>~{totalTokens} tokens added to every request</span>
        </div>
      )}
    </div>
  );
}
